import { useCallback, useMemo, useState } from "react";
import type {
  EntityItemConfig,
  EntityPoolConfig,
  Omittable,
} from "@salestrak/ui";
import {
  appendMissing,
  normalizeValue,
} from "#app/components/entity-selectors/utils";
import { usePoolSelection } from "#app/components/entity-selectors/use-pool-selection";
import {
  useMemberItemResolver,
  useUserPool,
  type UseUserPoolOptions,
} from "#app/components/entity-selectors/hooks/use-user-pool";

type UseMemberSelectionOptions = Omittable<{
  /** Selected member id(s). */
  value?: string | string[] | null;
  /** The caller's own copy of the selected members, carrying its
   *  decorations — preferred over the directory for a pinned selection. */
  seeds?: EntityItemConfig[];
}> &
  Pick<UseUserPoolOptions, "exclude" | "activeOnly" | "statuses">;

/**
 * THE member-picker data source — the paged, server-searched user pool plus
 * the selections for `value`, shared by every member selector (assignees,
 * reports-to, member fields).
 *
 * The pool is WINDOWED, so a selected member can sit on a page nobody has
 * scrolled to. Each one missing from the loaded pages is pinned above the
 * list from `seeds`, else from the member directory (which fetches ids
 * outside the bootstrap roster), so the current value always renders and can
 * be cleared. A pinned member stays pinned while selected, and leaves the
 * paged list when its page or a search brings it in, so it neither shifts as
 * pages load nor joins unrelated results.
 */
export function useMemberSelection({
  value,
  seeds,
  exclude,
  activeOnly,
  statuses,
}: UseMemberSelectionOptions = {}) {
  const { pool: userPool, loading } = useUserPool({ exclude, activeOnly, statuses });
  const resolveMember = useMemberItemResolver();
  const ids = useValueIds(value);
  const seedFor = useCallback(
    (id: string) => seeds?.find((seed) => seed.id === id),
    [seeds],
  );
  const { pool, selections, toIds } = usePinnedSelection(userPool, ids, resolveMember, seedFor);
  return { pool, selections, toIds, loading };
}

/** `value` as an id list, keyed by content so a `value` array rebuilt each
 *  render keeps the memos downstream. */
export function useValueIds(value: string | string[] | null | undefined): string[] {
  const valueKey = normalizeValue(value).join(",");
  return useMemo(() => (valueKey ? valueKey.split(",") : []), [valueKey]);
}

/**
 * A windowed `pool` with each selected id it does not list pinned above it,
 * plus the selections for `ids` and their multi-select write path (`toIds`,
 * see `usePoolSelection`). A pinned id stays pinned while selected and
 * leaves the paged list when a page or a search brings it in, so it neither
 * shifts as pages load nor joins unrelated results. `itemFor` names an id the
 * loaded rows do not hold; `preferredFor`, when given, wins over both.
 */
export function usePinnedSelection(
  pool: EntityPoolConfig,
  ids: string[],
  itemFor: (id: string) => EntityItemConfig | undefined,
  preferredFor?: (id: string) => EntityItemConfig | undefined,
) {
  const loaded = useMemo(
    () => new Map(pool.items.map((item) => [item.id, item])),
    [pool.items],
  );
  const [pinnedIds, setPinnedIds] = useState<string[]>([]);
  const nextPinnedIds = ids.filter((id) => pinnedIds.includes(id) || !loaded.has(id));
  if (nextPinnedIds.join(",") !== pinnedIds.join(",")) setPinnedIds(nextPinnedIds);

  const pinnedPool = useMemo<EntityPoolConfig>(() => {
    const pinnedItems = appendMissing(
      [],
      pinnedIds,
      (id) => preferredFor?.(id) ?? loaded.get(id) ?? itemFor(id),
    );
    if (pinnedItems.length === 0) return pool;
    const pinned = new Set(pinnedItems.map((item) => item.id));
    const items = pool.items.filter((item) => !pinned.has(item.id));
    const listedPinned = pool.items.length - items.length;
    return {
      ...pool,
      items,
      pinnedItems,
      total: pool.total == null ? pool.total : pool.total - listedPinned,
    };
  }, [pool, pinnedIds, loaded, itemFor, preferredFor]);

  const pools = useMemo(() => [pinnedPool], [pinnedPool]);
  const { selections, toIds } = usePoolSelection(pools, ids);
  return { pool: pinnedPool, selections, toIds };
}

/**
 * `roster` plus each of `ids` it does not hold — for lists whose groups come
 * from a server count (assignee bands, board columns): the roster is one
 * page, so an assignee past it would otherwise get no group. Ids that are not
 * members (a team, the unassigned bucket) resolve to nothing. A former member
 * is kept though the roster leaves them out: a counted id holds work, and a
 * band dropped for it hides that work from the grouped view.
 */
export function useMemberItemsWith(
  roster: EntityItemConfig[],
  ids: readonly string[],
): EntityItemConfig[] {
  const itemFor = useMemberItemResolver();
  return useMemo(() => appendMissing(roster, ids, itemFor), [roster, ids, itemFor]);
}
